(() => {
  if (customElements.get("trusted-product-reviews")) return;

  const IDENTIFIER_TYPE = "sku";
  const EMPTY_OBJECT = (value) => value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0;

  class TrustedProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector("[data-b-tpr-track]");
      this.template = this.querySelector("[data-b-tpr-card-template]");
      this.controls = this.querySelector("[data-b-tpr-controls]");
      this.prev = this.querySelector("[data-b-tpr-prev]");
      this.next = this.querySelector("[data-b-tpr-next]");
      this.progress = this.querySelector("[data-b-tpr-progress]");

      if (!this.track || !this.template) return;

      this.onScroll = this.updateControls.bind(this);
      this.onResize = this.updateControls.bind(this);
      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);
      this.prev?.addEventListener("click", () => this.scrollByPage(-1));
      this.next?.addEventListener("click", () => this.scrollByPage(1));
      this.cancelConsent = window.BaechleinPrivacy?.whenAllowed("marketing", () => {
        if (this.isConnected) this.load();
      });
    }

    disconnectedCallback() {
      this.cancelConsent?.();
      this.track?.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
    }

    async load() {
      const widgetId = this.dataset.widgetId?.trim();
      const sourceSkus = [...new Set((this.dataset.skus || "").split(",").map((sku) => sku.trim()).filter(Boolean))];
      if (!widgetId || sourceSkus.length === 0) return;

      try {
        const manifest = await this.fetchJson(
          `https://integrations.etrusted.com/manifests/v1/${encodeURIComponent(widgetId)}.json`
        );
        const productTemplate = manifest.dynamicFeeds?.products?.url;
        const reviewsTemplate = manifest.dynamicFeeds?.product_reviews?.url;
        const gradesTemplate = manifest.dynamicFeeds?.grades?.url;
        if (!reviewsTemplate || !gradesTemplate) return;

        const [configuration, channel] = await Promise.all([
          manifest.configuration?.url ? this.fetchOptional(manifest.configuration.url) : null,
          manifest.feeds?.channels?.url ? this.fetchOptional(manifest.feeds.channels.url) : null
        ]);
        const trackingQuery = configuration?.showUGC === true && channel?.id
          ? this.trackingQuery(widgetId, channel.id)
          : "";

        const resolvedSkus = productTemplate
          ? await this.resolveProductSkus(productTemplate, sourceSkus)
          : sourceSkus;
        const skus = resolvedSkus.length > 0 ? resolvedSkus : sourceSkus;

        const [reviewResults, gradeResults] = await Promise.all([
          Promise.all(skus.map((sku) => this.fetchOptional(
            `${this.feedUrl(reviewsTemplate, sku, true)}${trackingQuery}`
          ))),
          Promise.all(skus.map((sku) => this.fetchOptional(this.feedUrl(gradesTemplate, sku))))
        ]);

        const reviews = this.normalizeReviews(reviewResults);
        const grade = this.aggregateGrades(gradeResults, reviews);
        if (reviews.length === 0 || grade.count === 0) return;

        this.renderSummary(grade);
        this.renderReviews(reviews);
        this.querySelector("reveal-list")?.refreshItems?.();
        this.hidden = false;
        requestAnimationFrame(() => this.updateControls());
      } catch (error) {
        // The widget is configured to hide when empty; network failures follow the same behavior.
      }
    }

    async resolveProductSkus(template, sourceSkus) {
      const products = await Promise.all(
        sourceSkus.map((sku) => this.fetchOptional(this.feedUrl(template, sku)))
      );

      return [...new Set(
        products
          .flatMap((product) => Array.isArray(product) ? product : [product])
          .map((product) => product?.sku)
          .filter(Boolean)
      )];
    }

    feedUrl(template, sku, includeFilter = false) {
      let url = template
        .replace("$_PRODUCT_IDENTIFIER_TYPE", IDENTIFIER_TYPE)
        .replace("$_PRODUCT_IDENTIFIER", this.encodeSku(sku));
      if (includeFilter) url = url.replace("$_FILTER", "default/all");
      return url;
    }

    encodeSku(sku) {
      return Array.from(new TextEncoder().encode(sku), (byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    trackingQuery(widgetId, channelId) {
      const params = new URLSearchParams({
        widgetId,
        channelId,
        showUGC: "true"
      });
      return `?${params.toString()}`;
    }

    async fetchJson(url) {
      if (!window.BaechleinPrivacy?.allowed("marketing")) throw new Error("Consent required");
      const response = await fetch(url, { credentials: "omit" });
      if (!response.ok) throw new Error(`Trusted Shops request failed: ${response.status}`);
      const data = await response.json();
      if (EMPTY_OBJECT(data)) throw new Error("Trusted Shops returned no data");
      return data;
    }

    async fetchOptional(url) {
      try {
        return await this.fetchJson(url);
      } catch (error) {
        return null;
      }
    }

    normalizeReviews(results) {
      const reviews = results.flatMap((result) => Array.isArray(result) ? result : []).filter(Boolean);
      const deduplicated = new Map();

      reviews.forEach((review) => {
        const originalId = review.syndicated ? review.originalReview?.id : null;
        const key = originalId || review.id || `${review.submittedAt}-${review.title}-${review.comment}`;
        const existing = deduplicated.get(key);
        if (!existing || this.reviewTimestamp(review) > this.reviewTimestamp(existing)) {
          deduplicated.set(key, review);
        }
      });

      return [...deduplicated.values()].sort((a, b) => this.reviewTimestamp(b) - this.reviewTimestamp(a));
    }

    reviewTimestamp(review) {
      const timestamp = new Date(review.editedAt || review.submittedAt || 0).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    aggregateGrades(results, reviews) {
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let count = 0;

      results.forEach((result) => {
        const grade = result?.grades?.overall;
        if (!grade) return;
        count += Number(grade.count) || 0;
        distribution[1] += Number(grade.distribution?.oneStar) || 0;
        distribution[2] += Number(grade.distribution?.twoStars) || 0;
        distribution[3] += Number(grade.distribution?.threeStars) || 0;
        distribution[4] += Number(grade.distribution?.fourStars) || 0;
        distribution[5] += Number(grade.distribution?.fiveStars) || 0;
      });

      if (count === 0) {
        reviews.forEach((review) => {
          const rating = Math.max(1, Math.min(5, Math.round(Number(review.rating) || 0)));
          distribution[rating] += 1;
        });
        count = reviews.length;
      }

      const weightedTotal = Object.entries(distribution)
        .reduce((total, [rating, amount]) => total + Number(rating) * amount, 0);

      return {
        count,
        distribution,
        rating: count > 0 ? weightedTotal / count : 0
      };
    }

    renderSummary(grade) {
      const locale = document.documentElement.lang || "de-DE";
      const score = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(grade.rating);
      const count = new Intl.NumberFormat(locale).format(grade.count);
      const title = this.querySelector("[data-b-tpr-summary-title]");

      if (title) title.textContent = this.ratingLabel(grade.rating);
      this.setText("[data-b-tpr-summary-rating]", score);
      this.setText("[data-b-tpr-summary-count]", count);
      this.setText("[data-b-tpr-score]", score);
      this.setText("[data-b-tpr-count]", count);

      for (let rating = 1; rating <= 5; rating += 1) {
        const percentage = grade.count > 0 ? Math.round((grade.distribution[rating] / grade.count) * 100) : 0;
        const bar = this.querySelector(`[data-b-tpr-bar="${rating}"]`);
        if (bar) bar.style.width = `${percentage}%`;
        this.setText(`[data-b-tpr-percent="${rating}"]`, `${percentage}%`);
      }
    }

    ratingLabel(rating) {
      if (rating >= 4.5) return "Sehr gut";
      if (rating >= 3.5) return "Gut";
      if (rating >= 2.5) return "Befriedigend";
      if (rating >= 1.5) return "Ausreichend";
      return "Mangelhaft";
    }

    renderReviews(reviews) {
      const fragment = document.createDocumentFragment();

      reviews.forEach((review) => {
        const card = this.template.content.firstElementChild.cloneNode(true);
        const title = card.querySelector("[data-b-tpr-review-title]");
        const text = card.querySelector("[data-b-tpr-review-text]");
        const author = card.querySelector("[data-b-tpr-review-author]");
        const stars = card.querySelector("[data-b-tpr-review-stars]");
        const verified = card.querySelector("[data-b-tpr-review-verified]");

        if (this.dataset.profileUrl) card.href = this.dataset.profileUrl;
        else {
          card.removeAttribute("target");
          card.removeAttribute("rel");
        }
        title.textContent = review.title?.trim() || this.dataset.fallbackTitle;
        text.textContent = review.comment?.trim() || "";
        author.textContent = this.authorLine(review);
        this.renderStars(stars, review.rating);

        if (review.isVerified === true) {
          verified.hidden = false;
          verified.querySelector("[data-b-tpr-verified-text]").textContent = this.dataset.verifiedLabel;
        }

        fragment.appendChild(card);
      });

      this.track.replaceChildren(fragment);
      this.dataset.reviewCount = String(reviews.length);
    }

    renderStars(container, ratingValue) {
      const rating = Math.max(0, Math.min(5, Math.round(Number(ratingValue) || 0)));
      container.textContent = "★".repeat(rating) + "☆".repeat(5 - rating);
      container.setAttribute("aria-label", `${rating} von 5 Sternen`);
    }

    authorLine(review) {
      const consumer = review.consumer || {};
      const firstName = consumer.firstName?.trim();
      const lastName = consumer.lastName?.trim();
      const lastInitial = lastName ? `${lastName.charAt(0)}.` : "";
      const name = [firstName, lastInitial].filter(Boolean).join(" ") || this.dataset.fallbackAuthor;
      const city = consumer.city?.trim();
      const date = this.relativeDate(review.editedAt || review.submittedAt);
      return `${name}${city ? ` aus ${city}` : ""}${date ? `, ${date}` : ""}`;
    }

    relativeDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";

      const elapsedDays = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
      const locale = document.documentElement.lang || "de-DE";
      const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      if (elapsedDays < 30) return formatter.format(-elapsedDays, "day");
      if (elapsedDays < 365) return formatter.format(-Math.floor(elapsedDays / 30), "month");
      return formatter.format(-Math.floor(elapsedDays / 365), "year");
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    }

    scrollByPage(direction) {
      this.track.scrollBy({ left: this.track.clientWidth * 0.9 * direction, behavior: "smooth" });
    }

    updateControls() {
      if (!this.track || !this.progress) return;
      const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const scrollProgress = max > 0 ? this.track.scrollLeft / max : 0;
      const progressWidth = max > 0 ? Math.max(this.track.clientWidth / this.track.scrollWidth, 0.18) : 1;

      if (this.controls) this.controls.hidden = max <= 2;
      this.progress.style.width = `${progressWidth * 100}%`;
      this.progress.style.transform = `translateX(${scrollProgress * (100 / progressWidth - 100)}%)`;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  }

  customElements.define("trusted-product-reviews", TrustedProductReviews);
})();
